import React from 'react';
import { Flex, theme, Typography, ButtonProps } from 'antd';
import { CloseOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { PlanName, Support, isStarterPlanId } from '@prerender/billing-shared';

import Plan, { PLANS_DESCRIPTIONS } from './Plan';
import { formatCentsToDollars, longNumberFormatter, formatDays, formatHours } from '~library/numbers';
import { useEvent } from '~features/events/hooks/useEvent';
import { HubSpot } from '~features/CustomerSupport';
import { useTrial } from '~features/FreeTrial/useTrial';
import { useSelector, usePrerenderEmail } from '../../../hooks/usePrerenderUser';
import ButtonWithRole from '../../../components/ButtonWithRole';
import { BILLING_WRITE_REFUSED_ROLES } from '../../auth/roleCapabilities';
import { getPlanActions } from '../getPlanActions';
import { useSubscriptionState } from '../PlanStatus';

const SupportFeatures: Record<
  keyof typeof Support,
  {
    supportChannels: string;
    SLA: number;
    prioritySupport: string | boolean;
    insights: string | boolean;
    customReports: boolean;
  }
> = {
  low: {
    supportChannels: 'Chat (AI), Email / Ticket',
    SLA: 48, //hours
    prioritySupport: false,
    insights: false,
    customReports: false,
  },
  normal: {
    supportChannels: 'Chat (AI), Email / Ticket',
    SLA: 24, // hours
    prioritySupport: false,
    insights: false,
    customReports: false,
  },
  high: {
    supportChannels: 'Chat (AI → Human), Email / Ticket',
    SLA: 12, // hours
    prioritySupport: true,
    insights: 'Proactive Usage Monitoring',
    customReports: false,
  },
  enterprise: {
    supportChannels: 'Chat (Human), Email / Ticket',
    SLA: 2, // hours
    prioritySupport: 'Dedicated CSM',
    insights: 'QBRs & Custom Reporting',
    customReports: true,
  },
  none: {
    supportChannels: 'none',
    SLA: 48, // hours
    prioritySupport: false,
    insights: false,
    customReports: false,
  },
};

function Checkmark({ checked = false }: { checked?: boolean }) {
  return (
    <Typography.Text type={checked ? 'success' : 'danger'}>
      {checked ? <CheckOutlined /> : <CloseOutlined />}
    </Typography.Text>
  );
}

function DataRetentionDaysExport({
  dataRetentionDaysExport,
}: {
  dataRetentionDaysExport: Plan['dataRetentionDaysExport'];
}) {
  if (!dataRetentionDaysExport) return <Checkmark />;

  if (typeof dataRetentionDaysExport === 'number')
    return <Typography.Text type="secondary">{formatDays(dataRetentionDaysExport)}</Typography.Text>;

  const DataRetentionLabels = {
    'all-time': 'All-time',
  };

  return <Typography.Text type="secondary">{DataRetentionLabels[dataRetentionDaysExport]}</Typography.Text>;
}

function CardButton({
  onClick,
  highlighted,
  btnProps,
  children,
}: React.PropsWithChildren<{ onClick: () => void; highlighted?: boolean; btnProps?: ButtonProps }>) {
  return (
    <ButtonWithRole
      type={highlighted ? 'primary' : 'default'}
      disabledFor={BILLING_WRITE_REFUSED_ROLES}
      block
      onClick={onClick}
      {...btnProps}
    >
      {children}
    </ButtonWithRole>
  );
}

function CardBadge({ type }: { type: 'current' | 'mostPopular' }) {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        position: 'absolute',
        padding: `0px ${token.paddingXS}px`,
        top: 0,
        right: 0,
        backgroundColor: type === 'current' ? token.colorBorder : token.colorPrimary,
        borderBottomLeftRadius: token.borderRadius,
        color: type === 'current' ? token.colorTextBase : token.colorWhite,
      }}
    >
      <Typography.Text style={{ color: 'inherit' }}>{type === 'current' ? 'Current' : 'Most Popular'}</Typography.Text>
    </div>
  );
}

type PricingPlanCardProps = {
  plan: Plan;
  onClick: (plan: Plan) => void;
  current?: boolean;
  mostPopular?: boolean;
  showAiInsights?: boolean;
};
function PricingPlanCard({ plan, onClick, current, mostPopular, showAiInsights = false }: PricingPlanCardProps) {
  const { token } = theme.useToken();
  const { track } = useEvent();
  const userFirstName = useSelector((state) => state.prerenderUser.firstName);
  const userEmail = usePrerenderEmail();
  const [planState, paymentMethodState] = useSubscriptionState();
  const hubspotLink = HubSpot.createFormUrl(userFirstName || '', userEmail);
  const { isInTrial, hadTrialBefore, ...trialRest } = useTrial();
  const daysLeftOnTrial = trialRest.daysLeftOnTrial ?? 0;
  const isStarterPlan = isStarterPlanId(plan.id);
  const planActions = getPlanActions({
    planStatus: planState.status,
    isChargeable: paymentMethodState.isChargeable,
    isInTrial,
    isStarterPlan,
  });

  let daysLeftOnTrialText = '';
  if (isStarterPlan && current) {
    if (isInTrial) {
      if (daysLeftOnTrial <= 1) {
        daysLeftOnTrialText = '1 day left in trial';
      } else {
        daysLeftOnTrialText = `${Math.ceil(daysLeftOnTrial)} days left in trial`;
      }
    } else if (hadTrialBefore && !paymentMethodState.isChargeable) {
      daysLeftOnTrialText = 'Your trial has ended';
    }
  }

  const getMainCtaText = (planName: PlanName) => {
    if (!current) return `Switch to ${planName}`;

    if (planActions.showReactivate) return 'Reactivate plan';

    if (planActions.showActivateStarterNow) return 'Activate now';

    if (planActions.showKeepAccess) return 'Keep access';

    return 'Current';
  };

  const mainCtaDisabled =
    current && !planActions.showReactivate && !planActions.showActivateStarterNow && !planActions.showKeepAccess;

  return (
    <div
      style={{
        height: '100%',
        backgroundColor: mostPopular && !current ? token.colorPrimaryBg : token.colorBgBase,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        textAlign: 'center',
        minWidth: 260,
        padding: token.paddingXS,
        position: 'relative',
      }}
    >
      {(current || mostPopular) && <CardBadge type={current ? 'current' : 'mostPopular'} />}
      <div
        style={{
          borderBottom: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginLG,
          marginTop: token.marginLG,
        }}
      >
        <Typography.Paragraph strong style={{ margin: 0 }}>
          {plan.name}
        </Typography.Paragraph>
        <Typography.Text type="secondary">{PLANS_DESCRIPTIONS[plan.id] || '-'}</Typography.Text>
        <Typography.Paragraph strong style={{ marginTop: token.marginXS, fontSize: token.fontSizeHeading5 }}>
          {plan.contactUsToUse ? 'Custom' : `${formatCentsToDollars(plan.costInCents)}/mo`}
        </Typography.Paragraph>
      </div>
      <Flex vertical gap="middle" style={{ marginBottom: token.marginXXS }}>
        <Typography.Text type="secondary">
          {plan.contactUsToUse ? '+1M' : longNumberFormatter.format(plan.includedCaches)}
        </Typography.Text>
        <Typography.Text type="secondary">
          {plan.contactUsToUse ? 'Custom' : `${formatCentsToDollars(plan.addonCostInCents)} / 1000`}
        </Typography.Text>
        <Typography.Text type="secondary">
          {plan.cacheRefreshInterval[0] === plan.cacheRefreshInterval[1]
            ? formatDays(plan.cacheRefreshInterval[0])
            : `${formatDays(plan.cacheRefreshInterval[0])} - ${formatDays(plan.cacheRefreshInterval[1])}`}
        </Typography.Text>
        <Typography.Text type="secondary">
          {plan.domainSchedulerSettingsMaxRules ? (
            `${plan.domainSchedulerSettingsMaxRules} rules`
          ) : (
            <Checkmark checked={false} />
          )}
        </Typography.Text>
        <Typography.Text type="secondary">
          {!plan.sitemapRevisitInterval && '-'}

          {plan.sitemapRevisitInterval &&
            (plan.sitemapRevisitInterval[0] === plan.sitemapRevisitInterval[1]
              ? formatHours(plan.sitemapRevisitInterval[0])
              : `${formatHours(plan.sitemapRevisitInterval[0])} - ${formatHours(plan.sitemapRevisitInterval[1])}`)}
        </Typography.Text>

        <Typography.Text type="secondary">
          {plan.dataRetentionDays ? formatDays(plan.dataRetentionDays) : '-'}
        </Typography.Text>
        <Checkmark checked={plan.cacheLocation === 'both'} />
        <Checkmark checked={!!plan.auditLog} />

        <Typography.Text type="secondary">{SupportFeatures[plan.support].supportChannels}</Typography.Text>
        <Typography.Text type="secondary">{SupportFeatures[plan.support].SLA}h</Typography.Text>
        {typeof SupportFeatures[plan.support].prioritySupport === 'string' && (
          <Typography.Text type="secondary">{SupportFeatures[plan.support].prioritySupport}</Typography.Text>
        )}
        {typeof SupportFeatures[plan.support].prioritySupport !== 'string' && (
          <Checkmark checked={!!SupportFeatures[plan.support].prioritySupport} />
        )}
        {typeof SupportFeatures[plan.support].insights === 'string' && (
          <Typography.Text type="secondary">{SupportFeatures[plan.support].insights}</Typography.Text>
        )}
        {typeof SupportFeatures[plan.support].insights !== 'string' && (
          <Checkmark checked={!!SupportFeatures[plan.support].insights} />
        )}
        {showAiInsights && <Checkmark checked />}
        <Typography.Text type="secondary">
          {plan.searchPerformanceMaxDomains ? (
            `${plan.searchPerformanceMaxDomains} ${plan.searchPerformanceMaxDomains === 1 ? 'domain' : 'domains'}`
          ) : (
            <Checkmark checked={false} />
          )}
        </Typography.Text>
        <div style={{ visibility: daysLeftOnTrialText ? 'visible' : 'hidden' }}>
          <ClockCircleOutlined style={{ fontSize: 12, marginRight: 4, color: token.colorPrimary }} />
          <Typography.Text style={{ fontSize: 12, color: token.colorPrimary }}>
            {daysLeftOnTrialText || '-'}
          </Typography.Text>
        </div>
      </Flex>
      {plan.contactUsToUse ? (
        <a href={hubspotLink} target="_blank" rel="noreferrer">
          <CardButton
            onClick={() => track('Custom plan Contact us button clicked')}
            highlighted={mostPopular}
            btnProps={{ disabled: current }}
          >
            {current ? 'Current' : 'Get in touch'}
          </CardButton>
        </a>
      ) : (
        <CardButton
          onClick={() => onClick(plan)}
          highlighted={mostPopular && !current}
          btnProps={{ disabled: mainCtaDisabled }}
        >
          {getMainCtaText(plan.name)}
        </CardButton>
      )}
    </div>
  );
}

export default PricingPlanCard;
